import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Edit, Trash2, Building, Phone, Mail, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Location } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Badge from '../ui/Badge';

interface LocationManagerProps {
  onLocationSelect?: (location: Location) => void;
  selectedLocationId?: string;
  showActions?: boolean;
}

const LocationManager: React.FC<LocationManagerProps> = ({
  onLocationSelect,
  selectedLocationId,
  showActions = true
}) => {
  const { user } = useAuth();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    contact_person: '',
    phone: '',
    email: '',
    is_default: false,
    is_active: true
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .order('is_default', { ascending: false })
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const locationData = {
        ...formData,
        user_id: user?.id
      };

      if (editingLocation) {
        const { error } = await supabase
          .from('locations')
          .update(locationData)
          .eq('id', editingLocation.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('locations')
          .insert([locationData]);

        if (error) throw error;
      }

      setShowForm(false);
      setEditingLocation(null);
      resetForm();
      loadLocations();
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      description: location.description || '',
      address: location.address || '',
      contact_person: location.contact_person || '',
      phone: location.phone || '',
      email: location.email || '',
      is_default: location.is_default,
      is_active: location.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (location: Location) => {
    if (location.is_default) {
      alert('Cannot delete the default location');
      return;
    }

    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        const { error } = await supabase
          .from('locations')
          .delete()
          .eq('id', location.id);

        if (error) throw error;
        loadLocations();
      } catch (error) {
        console.error('Error deleting location:', error);
      }
    }
  };

  const handleSetDefault = async (location: Location) => {
    try {
      // Remove default from all locations
      await supabase
        .from('locations')
        .update({ is_default: false })
        .eq('user_id', user?.id);

      // Set new default
      const { error } = await supabase
        .from('locations')
        .update({ is_default: true })
        .eq('id', location.id);

      if (error) throw error;
      loadLocations();
    } catch (error) {
      console.error('Error setting default location:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      contact_person: '',
      phone: '',
      email: '',
      is_default: false,
      is_active: true
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showActions && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Storage Locations</h3>
          <Button
            onClick={() => {
              setEditingLocation(null);
              resetForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Location
          </Button>
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </CardTitle>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Location Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                />
                <Input
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  fullWidth
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  fullWidth
                />
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  fullWidth
                />
              </div>

              <Input
                label="Address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                fullWidth
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
                {!editingLocation?.is_default && (
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Set as default</span>
                  </label>
                )}
              </div>
            </CardContent>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingLocation(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingLocation ? 'Update' : 'Create'} Location
              </Button>
            </div>
          </form>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locations.map((location) => (
          <Card
            key={location.id}
            className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
              selectedLocationId === location.id ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => onLocationSelect?.(location)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-50 rounded-full mr-3">
                    <Building className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{location.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      {location.is_default && (
                        <Badge variant="primary">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </Badge>
                      )}
                      <Badge variant={location.is_active ? 'success' : 'secondary'}>
                        {location.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
                {showActions && (
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(location);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {!location.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(location);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {location.description && (
                <p className="text-sm text-gray-600 mb-3">{location.description}</p>
              )}

              <div className="space-y-2">
                {location.address && (
                  <div className="flex items-center text-sm text-gray-500">
                    <MapPin className="h-4 w-4 mr-2" />
                    {location.address}
                  </div>
                )}
                {location.contact_person && (
                  <div className="flex items-center text-sm text-gray-500">
                    <span className="font-medium mr-2">Contact:</span>
                    {location.contact_person}
                  </div>
                )}
                {location.phone && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Phone className="h-4 w-4 mr-2" />
                    {location.phone}
                  </div>
                )}
                {location.email && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Mail className="h-4 w-4 mr-2" />
                    {location.email}
                  </div>
                )}
              </div>

              {showActions && !location.is_default && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefault(location);
                    }}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Star className="h-4 w-4 mr-1" />
                    Set as Default
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {locations.length === 0 && (
        <div className="text-center py-8">
          <Building className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No locations found</h3>
          <p className="text-gray-500 mb-4">Add your first storage location to get started</p>
          {showActions && (
            <Button
              onClick={() => {
                setEditingLocation(null);
                resetForm();
                setShowForm(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationManager;